/**
 * <s-card-swatches> — colour swatches on a product card that swap the card's
 * photo in place instead of navigating to the product.
 *
 * Required child markup:
 *   button[data-swatch]  — one per colour. Carries:
 *     data-src     — image URL for the colour's photo
 *     data-srcset  — responsive srcset for that photo
 *     data-url     — the product URL with that colour's variant selected
 *     data-name    — the colour name to show
 *     aria-pressed — "true" on the selected colour (the stylesheet draws its ring)
 *   [data-swatch-name] — text element that shows the selected colour's name
 *
 * On press, the photo inside the closest <article> card is swapped, every
 * link inside that card is pointed at the chosen colour, the pressed state
 * moves to the chosen swatch, and the name text updates.
 */

/*
 * Sister colours (one product per colour, same title). When the card's product holds a single
 * colour, the snippet sets data-sibling-title. We look up other products with exactly the same
 * title through predictive search, read each one's single colour from /products/<handle>.js,
 * and replace the lone swatch with one swatch per sister colour. Pressing a sister swatch swaps
 * the card photo and points the card links at that colour's product.
 */
const siblingCache = new Map();

function capitalise(value) {
  const lower = String(value).toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function sizedUrl(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

// Reads the whole catalogue once per page (/products.json, 250 per page) so new products link straight away.
let catalogPromise = null;
function getCatalog() {
  if (!catalogPromise) {
    catalogPromise = (async () => {
      const all = [];
      for (let page = 1; page <= 4; page++) {
        const res = await fetch(`/products.json?limit=250&page=${page}`, { headers: { Accept: 'application/json' } });
        if (!res.ok) break;
        const data = await res.json();
        const products = data?.products ?? [];
        all.push(...products);
        if (products.length < 250) break;
      }
      return all;
    })().catch(() => []);
  }
  return catalogPromise;
}

async function fetchSiblings(title) {
  const wanted = title.trim().toLowerCase();
  const catalog = await getCatalog();
  const matches = catalog.filter((p) => String(p.title).trim().toLowerCase() === wanted);
  if (matches.length < 2) return [];
  return matches
    .map((product) => {
      const colourOption = (product.options || []).find((o) => ['colour', 'color'].includes(String(o.name).toLowerCase()));
      if (!colourOption || colourOption.values.length !== 1) return null;
      return {
        handle: product.handle,
        url: `/products/${product.handle}`,
        name: capitalise(colourOption.values[0]),
        image: product.images?.[0]?.src || '',
      };
    })
    .filter(Boolean);
}

function getSiblings(title) {
  if (!siblingCache.has(title)) siblingCache.set(title, fetchSiblings(title).catch(() => []));
  return siblingCache.get(title);
}

class SCardSwatches extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#ac.signal });
    if (this.dataset.siblingTitle) this.#loadSiblings();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('button[data-swatch]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;
    event.preventDefault();
    if (button.getAttribute('aria-pressed') === 'true') return;

    const card = this.closest('article');
    const { src, srcset, url, name } = button.dataset;

    if (card && src) {
      const image = card.querySelector('img');
      if (image instanceof HTMLImageElement) {
        if (srcset) image.srcset = srcset;
        image.src = src;
      }
    }

    if (card && url) {
      for (const link of card.querySelectorAll('a[href]')) {
        if (link instanceof HTMLAnchorElement) link.href = url;
      }
    }

    for (const swatch of this.querySelectorAll('button[data-swatch]')) {
      swatch.setAttribute('aria-pressed', String(swatch === button));
    }

    const nameEl = this.querySelector('[data-swatch-name]');
    if (nameEl && name) nameEl.textContent = name;

    // Sister colours are separate products: hide the hover photo (it belongs to the original colour) unless the card's own colour is back.
    if (card && button.hasAttribute('data-sibling')) {
      const isOwn = url === `/products/${this.dataset.handle}`;
      card.querySelectorAll('img').forEach((img, index) => {
        if (index > 0) img.hidden = !isOwn;
      });
    }
  }

  async #loadSiblings() {
    const title = this.dataset.siblingTitle;
    const ownHandle = this.dataset.handle;
    if (!title) return;
    const siblings = await getSiblings(title);
    if (siblings.length < 2 || !siblings.some((s) => s.handle === ownHandle)) return;
    const list = this.querySelector('ul');
    if (!list) return;
    const ordered = [...siblings.filter((s) => s.handle === ownHandle), ...siblings.filter((s) => s.handle !== ownHandle)];
    list.replaceChildren(
      ...ordered.map((sibling) => {
        const item = document.createElement('li');
        item.className = 'flex';
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.swatch = '';
        button.dataset.sibling = '';
        button.dataset.name = sibling.name;
        button.dataset.url = sibling.url;
        if (sibling.image) {
          button.dataset.src = sizedUrl(sibling.image, 750);
          button.dataset.srcset = [375, 550, 750, 1100, 1500].map((w) => `${sizedUrl(sibling.image, w)} ${w}w`).join(', ');
          button.style.backgroundImage = `url("${sizedUrl(sibling.image, 300)}")`;
          button.style.backgroundSize = '1000% auto';
          button.style.backgroundPosition = '38% 60%';
          button.style.backgroundRepeat = 'no-repeat';
        }
        button.setAttribute('aria-pressed', String(sibling.handle === ownHandle));
        button.title = sibling.name;
        const ownButton = this.querySelector('button[data-swatch]');
        button.className = ownButton ? ownButton.className : '';
        const label = document.createElement('span');
        label.className = 'sr-only';
        label.textContent = sibling.name;
        button.append(label);
        item.append(button);
        return item;
      }),
    );
  }
}

customElements.define('s-card-swatches', SCardSwatches);
